import React, { useContext, useMemo, useState } from 'react';

import { MdDelete } from 'react-icons/md';

import { getEnvironmentApiUrl } from '../../../services/General';
import { getFullName } from '../../../services/getPatientAtribute';

import { useToast } from '../../../hooks/useToast';
import useHttp from '../../../hooks/useHttp';

import PatientsContext from '../../../context/PatientsContext';

import PreconfigActionButton from '../PreconfigActionButton/PreconfigActionButton';
import DialogDuplicateDelete from '../DialogDuplicateDelete/DialogDuplicateDelete';
import Modal from '../../UI/Modal/Modal';

import { Preconfiguration } from '../../../interfaces/Preconfigurations';
import { Patient } from '../../../interfaces/Patient';
import { useTranslation } from 'react-i18next';

import styles from './DeletePreconfigButton.module.css';

type PatientCase = NonNullable<Patient['cases']>[number];

interface LinkedCaseRow {
    patient: Patient
    caseItem: PatientCase
}

interface Props {
    preconfiguration: Preconfiguration
    deletePreconfig: (id: Preconfiguration['id']) => void
    caseIdsLinked?: number[]
}

const DeletePreconfigButton: React.FC<Props> = ({
    preconfiguration,
    deletePreconfig,
    caseIdsLinked = []
}) => {
    const [showDeletePreconfig, setShowDeletePreconfig] = useState(false);

    const toast = useToast();
    const { t } = useTranslation();
    const patientsContext = useContext(PatientsContext);

    const { sendRequest } = useHttp<Pick<Preconfiguration, 'id' | 'name'>>();

    const hasAssignments = caseIdsLinked.length > 0;

    const linkedCases = useMemo<LinkedCaseRow[]>(() => {
        const linkedSet = new Set(caseIdsLinked);

        return patientsContext.patients.flatMap((patient) =>
            (patient.cases ?? [])
                .filter((caseItem) => linkedSet.has(caseItem.id))
                .map((caseItem) => ({
                    patient,
                    caseItem,
                }))
        );
    }, [caseIdsLinked, patientsContext.patients]);

    const handleClickDelete = (evt: React.FormEvent) => {
        evt.stopPropagation();
        setShowDeletePreconfig(true);
    };

    const handleDelete = () => {
        sendRequest({
            url: getEnvironmentApiUrl() + 'preconfig/' + preconfiguration.id,
            method: 'DELETE',
        }, () => {
            deletePreconfig(preconfiguration.id);
        }, () => {
            toast.error(t('error_deleting_preconfiguration'));
        });

        setShowDeletePreconfig(false);
    };

    const linkedPatientsText = useMemo(() => {
        if (!caseIdsLinked.length) return '';

        const linkedSet = new Set(caseIdsLinked);

        const names = patientsContext.patients.flatMap((patient) =>
            (patient.cases ?? [])
                .filter((caseItem) => linkedSet.has(caseItem.id))
                .map((caseItem) =>
                    `${getFullName(patient)} - ${caseItem.hospital_case ?? `Case ${caseItem.id}`}`
                )
        );

        return names.join('\n');
    }, [caseIdsLinked, patientsContext.patients]);

    return (
        <>
            <PreconfigActionButton
                type='button'
                onClick={handleClickDelete}
            >
                <MdDelete />
            </PreconfigActionButton>

            <Modal show={showDeletePreconfig} setShowModal={setShowDeletePreconfig}>
                <div className={styles.container}>
                    <DialogDuplicateDelete
                        onCancel={() => setShowDeletePreconfig(false)}
                        onConfirm={handleDelete}
                        title={
                            hasAssignments
                                ? `${t('sure_delete_preconfig_with_patients')}\n\n${linkedPatientsText}`
                                : t('sure_delete_preconfig')
                        }
                    />
                </div>
            </Modal>
        </>
    );
};

export default DeletePreconfigButton;